// Page registry: one route per page of shared/design/PAGES.md. The route is `/` + slug, which is
// also the `path` the API returns in NeedsConfig `missing[]` (e.g. /costs-markup?symbol=XAUUSD).
import { t } from '../i18n';

export type Section = 'Global' | 'Auth' | 'Launcher' | 'Monitor' | 'Insight' | 'Dux FX' | 'Settings';
export interface PageDef {
  id: string;
  slug: string;
  title: string;
  section: Section;
  /** design file in shared/design */
  design: string;
  /** SideNav item marked current on this page */
  nav: string | null;
  /** permission needed to open the page (GET /auth/session permissions); null = every signed-in user */
  perm: string | null;
  /** rendered inside the app shell (TopBar + SideNav) */
  shell: boolean;
}

export const PAGES: PageDef[] = [
  {
    id: 'SHL-01',
    slug: 'app-shell',
    title: 'App shell',
    section: 'Global',
    design: 'SHL-01_app-shell.html',
    nav: null,
    perm: null,
    shell: true,
  },
  {
    id: 'SHL-02',
    slug: 'global-overlays',
    title: "Toasts, alert drawer, What's New modal",
    section: 'Global',
    design: 'SHL-02_global-overlays.html',
    nav: 'dashboard',
    perm: null,
    shell: true,
  },
  {
    id: 'SHL-03',
    slug: 'system-states',
    title: 'Empty, error, stale, no-permission, 404',
    section: 'Global',
    design: 'SHL-03_system-states.html',
    nav: null,
    perm: null,
    shell: true,
  },
  {
    id: 'SHL-04',
    slug: 'licence-states',
    title: 'Licence banners, read-only, locked',
    section: 'Global',
    design: 'SHL-04_licence-states.html',
    nav: 'dashboard',
    perm: null,
    shell: true,
  },
  {
    id: 'AUTH-01',
    slug: 'login',
    title: 'Sign in',
    section: 'Auth',
    design: 'AUTH-01_login.html',
    nav: null,
    perm: null,
    shell: false,
  },
  {
    id: 'AUTH-02',
    slug: 'otp-email',
    title: 'Email code',
    section: 'Auth',
    design: 'AUTH-02_otp-email.html',
    nav: null,
    perm: null,
    shell: false,
  },
  {
    id: 'AUTH-03',
    slug: 'otp-authenticator',
    title: 'Authenticator code',
    section: 'Auth',
    design: 'AUTH-03_otp-authenticator.html',
    nav: null,
    perm: null,
    shell: false,
  },
  {
    id: 'AUTH-04',
    slug: 'forgot-password',
    title: 'Forgot password',
    section: 'Auth',
    design: 'AUTH-04_forgot-password.html',
    nav: null,
    perm: null,
    shell: false,
  },
  {
    id: 'AUTH-05',
    slug: 'reset-password',
    title: 'Reset password',
    section: 'Auth',
    design: 'AUTH-05_reset-password.html',
    nav: null,
    perm: null,
    shell: false,
  },
  {
    id: 'AUTH-06',
    slug: 'welcome',
    title: 'First-login welcome',
    section: 'Auth',
    design: 'AUTH-06_welcome.html',
    nav: null,
    perm: null,
    shell: false,
  },
  {
    id: 'AUTH-07',
    slug: 'connect-consent',
    title: 'Connect an AI app',
    section: 'Auth',
    design: 'AUTH-07_connect-consent.html',
    nav: null,
    perm: null,
    shell: false,
  },
  {
    id: 'LCH-01',
    slug: 'setup-wizard',
    title: 'Instance setup wizard',
    section: 'Launcher',
    design: 'LCH-01_setup-wizard.html',
    nav: 'setup',
    perm: 'launcher.setup',
    shell: true,
  },
  {
    id: 'LCH-02',
    slug: 'languages',
    title: 'Languages',
    section: 'Launcher',
    design: 'LCH-02_languages.html',
    nav: 'languages',
    perm: 'launcher.languages',
    shell: true,
  },
  {
    id: 'LCH-03',
    slug: 'instance-licence',
    title: 'Instance and licence',
    section: 'Launcher',
    design: 'LCH-03_instance-licence.html',
    nav: 'licence',
    perm: 'launcher.licence',
    shell: true,
  },
  {
    id: 'LCH-04',
    slug: 'system-health',
    title: 'System health',
    section: 'Launcher',
    design: 'LCH-04_system-health.html',
    nav: 'health',
    perm: 'page.system_health',
    shell: true,
  },
  {
    id: 'DSH-01',
    slug: 'dashboard',
    title: 'Dashboard',
    section: 'Monitor',
    design: 'DSH-01_dashboard.html',
    nav: 'dashboard',
    perm: 'page.dashboard',
    shell: true,
  },
  {
    id: 'EXP-01',
    slug: 'exposure-overview',
    title: 'Exposure overview',
    section: 'Monitor',
    design: 'EXP-01_exposure-overview.html',
    nav: 'exposure',
    perm: 'page.exposure',
    shell: true,
  },
  {
    id: 'EXP-02',
    slug: 'exposure-symbol',
    title: 'Symbol exposure detail',
    section: 'Monitor',
    design: 'EXP-02_exposure-symbol.html',
    nav: 'exposure',
    perm: 'page.exposure',
    shell: true,
  },
  {
    id: 'EXP-03',
    slug: 'var-stress',
    title: 'VaR and stress tests',
    section: 'Monitor',
    design: 'EXP-03_var-stress.html',
    nav: 'exposure',
    perm: 'page.exposure',
    shell: true,
  },
  {
    id: 'EXP-04',
    slug: 'ab-book-summary',
    title: 'A-Book / B-Book summary',
    section: 'Monitor',
    design: 'EXP-04_ab-book-summary.html',
    nav: 'exposure',
    perm: 'page.ab_book',
    shell: true,
  },
  {
    id: 'ACC-01',
    slug: 'accounts-list',
    title: 'Accounts',
    section: 'Monitor',
    design: 'ACC-01_accounts-list.html',
    nav: 'accounts',
    perm: 'page.accounts',
    shell: true,
  },
  {
    id: 'ACC-02',
    slug: 'accounts-insights',
    title: 'Accounts insights',
    section: 'Monitor',
    design: 'ACC-02_accounts-insights.html',
    nav: 'accounts',
    perm: 'page.accounts',
    shell: true,
  },
  {
    id: 'ACC-03',
    slug: 'groups-list',
    title: 'Groups',
    section: 'Monitor',
    design: 'ACC-03_groups-list.html',
    nav: 'accounts',
    perm: 'page.accounts',
    shell: true,
  },
  {
    id: 'ACC-04',
    slug: 'group-detail',
    title: 'Group detail',
    section: 'Monitor',
    design: 'ACC-04_group-detail.html',
    nav: 'accounts',
    perm: 'page.accounts',
    shell: true,
  },
  {
    id: 'TRD-01',
    slug: 'traders',
    title: 'Traders',
    section: 'Monitor',
    design: 'TRD-01_traders.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-02',
    slug: 'account-overview',
    title: 'Account · Overview',
    section: 'Monitor',
    design: 'TRD-02_account-overview.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-03',
    slug: 'account-trades',
    title: 'Account · Trades',
    section: 'Monitor',
    design: 'TRD-03_account-trades.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-04',
    slug: 'account-ab-history',
    title: 'Account · A/B history',
    section: 'Monitor',
    design: 'TRD-04_account-ab-history.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-05',
    slug: 'account-revenue',
    title: 'Account · Revenue',
    section: 'Monitor',
    design: 'TRD-05_account-revenue.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-06',
    slug: 'account-behaviour',
    title: 'Account · Behaviour',
    section: 'Monitor',
    design: 'TRD-06_account-behaviour.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-07',
    slug: 'account-access',
    title: 'Account · Access and devices',
    section: 'Monitor',
    design: 'TRD-07_account-access.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-08',
    slug: 'account-linked',
    title: 'Account · Linked accounts',
    section: 'Monitor',
    design: 'TRD-08_account-linked.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TRD-09',
    slug: 'account-case-log',
    title: 'Account · Case log',
    section: 'Monitor',
    design: 'TRD-09_account-case-log.html',
    nav: 'traders',
    perm: 'page.traders',
    shell: true,
  },
  {
    id: 'TOX-01',
    slug: 'toxic-overview',
    title: 'Toxic flow overview',
    section: 'Monitor',
    design: 'TOX-01_toxic-overview.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-02',
    slug: 'markout',
    title: 'Markout analysis',
    section: 'Monitor',
    design: 'TOX-02_markout.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-03',
    slug: 'coordinated-groups',
    title: 'Coordinated groups',
    section: 'Monitor',
    design: 'TOX-03_coordinated-groups.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-04',
    slug: 'coordinated-group-detail',
    title: 'Coordinated group detail',
    section: 'Monitor',
    design: 'TOX-04_coordinated-group-detail.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-05',
    slug: 'news-trading',
    title: 'News trading',
    section: 'Monitor',
    design: 'TOX-05_news-trading.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-06',
    slug: 'bonus-credit',
    title: 'Bonus and credit abuse',
    section: 'Monitor',
    design: 'TOX-06_bonus-credit.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-07',
    slug: 'swap-free',
    title: 'Swap-free accounts',
    section: 'Monitor',
    design: 'TOX-07_swap-free.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-08',
    slug: 'access-intelligence',
    title: 'Access intelligence',
    section: 'Monitor',
    design: 'TOX-08_access-intelligence.html',
    nav: 'toxic',
    perm: 'page.toxic',
    shell: true,
  },
  {
    id: 'TOX-09',
    slug: 'detection-rules',
    title: 'Detection rules',
    section: 'Monitor',
    design: 'TOX-09_detection-rules.html',
    nav: 'toxic',
    perm: 'page.detection_rules',
    shell: true,
  },
  {
    id: 'CAS-01',
    slug: 'cases-list',
    title: 'Cases',
    section: 'Monitor',
    design: 'CAS-01_cases-list.html',
    nav: 'cases',
    perm: 'page.cases',
    shell: true,
  },
  {
    id: 'CAS-02',
    slug: 'case-detail',
    title: 'Case detail',
    section: 'Monitor',
    design: 'CAS-02_case-detail.html',
    nav: 'cases',
    perm: 'page.cases',
    shell: true,
  },
  {
    id: 'CAS-03',
    slug: 'evidence-snapshot',
    title: 'Evidence snapshot',
    section: 'Monitor',
    design: 'CAS-03_evidence-snapshot.html',
    nav: 'cases',
    perm: 'page.cases',
    shell: true,
  },
  {
    id: 'CAS-04',
    slug: 'action-dialogs',
    title: 'Action dialogs',
    section: 'Monitor',
    design: 'CAS-04_action-dialogs.html',
    nav: 'cases',
    perm: 'page.cases',
    shell: true,
  },
  {
    id: 'CAS-05',
    slug: 'approvals',
    title: 'Pending approvals',
    section: 'Monitor',
    design: 'CAS-05_approvals.html',
    nav: 'cases',
    perm: 'page.cases',
    shell: true,
  },
  {
    id: 'ALR-01',
    slug: 'alerts',
    title: 'Alerts',
    section: 'Monitor',
    design: 'ALR-01_alerts.html',
    nav: 'alerts',
    perm: 'page.alerts',
    shell: true,
  },
  {
    id: 'ALR-02',
    slug: 'alert-detail',
    title: 'Alert detail',
    section: 'Monitor',
    design: 'ALR-02_alert-detail.html',
    nav: 'alerts',
    perm: 'page.alerts',
    shell: true,
  },
  {
    id: 'ALR-03',
    slug: 'alert-rules',
    title: 'Alert rules',
    section: 'Monitor',
    design: 'ALR-03_alert-rules.html',
    nav: 'alerts',
    perm: 'page.alert_rules',
    shell: true,
  },
  {
    id: 'ALR-04',
    slug: 'alert-rule-editor',
    title: 'Alert rule editor',
    section: 'Monitor',
    design: 'ALR-04_alert-rule-editor.html',
    nav: 'alerts',
    perm: 'page.alert_rules',
    shell: true,
  },
  {
    id: 'REV-01',
    slug: 'revenue-overview',
    title: 'Revenue overview',
    section: 'Insight',
    design: 'REV-01_revenue-overview.html',
    nav: 'revenue',
    perm: 'page.revenue',
    shell: true,
  },
  {
    id: 'REV-02',
    slug: 'revenue-breakdown',
    title: 'Revenue breakdown',
    section: 'Insight',
    design: 'REV-02_revenue-breakdown.html',
    nav: 'revenue',
    perm: 'page.revenue',
    shell: true,
  },
  {
    id: 'REV-03',
    slug: 'swap-reconciliation',
    title: 'Swap reconciliation',
    section: 'Insight',
    design: 'REV-03_swap-reconciliation.html',
    nav: 'revenue',
    perm: 'page.revenue',
    shell: true,
  },
  {
    id: 'RPT-01',
    slug: 'reports-library',
    title: 'Reports',
    section: 'Insight',
    design: 'RPT-01_reports-library.html',
    nav: 'reports',
    perm: 'page.reports',
    shell: true,
  },
  {
    id: 'RPT-02',
    slug: 'report-viewer',
    title: 'Report viewer',
    section: 'Insight',
    design: 'RPT-02_report-viewer.html',
    nav: 'reports',
    perm: 'page.reports',
    shell: true,
  },
  {
    id: 'RPT-03',
    slug: 'scheduled-reports',
    title: 'Scheduled reports',
    section: 'Insight',
    design: 'RPT-03_scheduled-reports.html',
    nav: 'reports',
    perm: 'page.reports',
    shell: true,
  },
  {
    id: 'AI-01',
    slug: 'ask-brainux',
    title: 'Ask Brainux',
    section: 'Insight',
    design: 'AI-01_ask-brainux.html',
    nav: 'ask',
    perm: 'page.ai',
    shell: true,
  },
  {
    id: 'AI-02',
    slug: 'daily-brief',
    title: 'Daily brief',
    section: 'Insight',
    design: 'AI-02_daily-brief.html',
    nav: 'ask',
    perm: 'page.ai',
    shell: true,
  },
  {
    id: 'SUG-01',
    slug: 'suggestions',
    title: 'Suggestions',
    section: 'Insight',
    design: 'SUG-01_suggestions.html',
    nav: 'suggestions',
    perm: 'page.suggestions',
    shell: true,
  },
  {
    id: 'NEW-01',
    slug: 'whats-new',
    title: "What's New · Dux FX",
    section: 'Dux FX',
    design: 'NEW-01_whats-new.html',
    nav: 'news',
    perm: 'page.whats_new',
    shell: true,
  },
  {
    id: 'NEW-02',
    slug: 'whats-new-detail',
    title: "What's New item",
    section: 'Dux FX',
    design: 'NEW-02_whats-new-detail.html',
    nav: 'news',
    perm: 'page.whats_new',
    shell: true,
  },
  {
    id: 'SET-01',
    slug: 'users',
    title: 'Users',
    section: 'Settings',
    design: 'SET-01_users.html',
    nav: 'settings',
    perm: 'page.settings.users',
    shell: true,
  },
  {
    id: 'SET-02',
    slug: 'roles',
    title: 'Roles and permissions',
    section: 'Settings',
    design: 'SET-02_roles.html',
    nav: 'settings',
    perm: 'page.settings.roles',
    shell: true,
  },
  {
    id: 'SET-03',
    slug: 'notification-channels',
    title: 'Notification channels',
    section: 'Settings',
    design: 'SET-03_notification-channels.html',
    nav: 'settings',
    perm: 'page.settings.channels',
    shell: true,
  },
  {
    id: 'SET-04',
    slug: 'profile',
    title: 'My profile',
    section: 'Settings',
    design: 'SET-04_profile.html',
    nav: 'settings',
    perm: null,
    shell: true,
  },
  {
    id: 'SET-05',
    slug: 'book-mapping',
    title: 'Groups, books and symbols',
    section: 'Settings',
    design: 'SET-05_book-mapping.html',
    nav: 'settings',
    perm: 'page.settings.mapping',
    shell: true,
  },
  {
    id: 'SET-06',
    slug: 'costs-markup',
    title: 'Costs · Markup',
    section: 'Settings',
    design: 'SET-06_costs-markup.html',
    nav: 'settings',
    perm: 'page.settings.costs',
    shell: true,
  },
  {
    id: 'SET-07',
    slug: 'costs-lp-swap',
    title: 'Costs · LP swap',
    section: 'Settings',
    design: 'SET-07_costs-lp-swap.html',
    nav: 'settings',
    perm: 'page.settings.costs',
    shell: true,
  },
  {
    id: 'SET-08',
    slug: 'costs-commission',
    title: 'Costs · Commission',
    section: 'Settings',
    design: 'SET-08_costs-commission.html',
    nav: 'settings',
    perm: 'page.settings.costs',
    shell: true,
  },
  {
    id: 'SET-09',
    slug: 'general',
    title: 'General settings',
    section: 'Settings',
    design: 'SET-09_general.html',
    nav: 'settings',
    perm: 'page.settings.general',
    shell: true,
  },
  {
    id: 'SET-10',
    slug: 'setup-health',
    title: 'Setup health',
    section: 'Settings',
    design: 'SET-10_setup-health.html',
    nav: 'settings',
    perm: 'page.settings.setup_health',
    shell: true,
  },
  {
    id: 'SET-11',
    slug: 'data-api',
    title: 'Data API keys',
    section: 'Settings',
    design: 'SET-11_data-api.html',
    nav: 'settings',
    perm: 'page.settings.data_api',
    shell: true,
  },
  {
    id: 'SET-12',
    slug: 'email-templates',
    title: 'Client email templates',
    section: 'Settings',
    design: 'SET-12_email-templates.html',
    nav: 'settings',
    perm: 'page.settings.email_templates',
    shell: true,
  },
  {
    id: 'SET-13',
    slug: 'audit-log',
    title: 'Audit log',
    section: 'Settings',
    design: 'SET-13_audit-log.html',
    nav: 'settings',
    perm: 'page.settings.audit',
    shell: true,
  },
  {
    id: 'SET-14',
    slug: 'ai-integrations',
    title: 'AI & integrations',
    section: 'Settings',
    design: 'SET-14_ai-integrations.html',
    nav: 'settings',
    // open with page.settings.ai or the "Connect AI apps" permission; the page checks which tabs show
    perm: null,
    shell: true,
  },
];

export const PAGE_BY_ID: Record<string, PageDef> = Object.fromEntries(PAGES.map((p) => [p.id, p]));
const TITLES: Record<string, () => string> = {
  'SHL-01': () => t('app.page.shl-01', 'App shell'),
  'SHL-02': () => t('app.page.shl-02', "Toasts, alert drawer, What's New modal"),
  'SHL-03': () => t('app.page.shl-03', 'Empty, error, stale, no-permission, 404'),
  'SHL-04': () => t('app.page.shl-04', 'Licence banners, read-only, locked'),
  'AUTH-01': () => t('app.page.auth-01', 'Sign in'),
  'AUTH-02': () => t('app.page.auth-02', 'Email code'),
  'AUTH-03': () => t('app.page.auth-03', 'Authenticator code'),
  'AUTH-04': () => t('app.page.auth-04', 'Forgot password'),
  'AUTH-05': () => t('app.page.auth-05', 'Reset password'),
  'AUTH-06': () => t('app.page.auth-06', 'First-login welcome'),
  'AUTH-07': () => t('app.page.auth-07', 'Connect an AI app'),
  'LCH-01': () => t('app.page.lch-01', 'Instance setup wizard'),
  'LCH-02': () => t('app.page.lch-02', 'Languages'),
  'LCH-03': () => t('app.page.lch-03', 'Instance and licence'),
  'LCH-04': () => t('app.page.lch-04', 'System health'),
  'DSH-01': () => t('app.page.dsh-01', 'Dashboard'),
  'EXP-01': () => t('app.page.exp-01', 'Exposure overview'),
  'EXP-02': () => t('app.page.exp-02', 'Symbol exposure detail'),
  'EXP-03': () => t('app.page.exp-03', 'VaR and stress tests'),
  'EXP-04': () => t('app.page.exp-04', 'A-Book / B-Book summary'),
  'ACC-01': () => t('app.page.acc-01', 'Accounts'),
  'ACC-02': () => t('app.page.acc-02', 'Accounts insights'),
  'ACC-03': () => t('app.page.acc-03', 'Groups'),
  'ACC-04': () => t('app.page.acc-04', 'Group detail'),
  'TRD-01': () => t('app.page.trd-01', 'Traders'),
  'TRD-02': () => t('app.page.trd-02', 'Account · Overview'),
  'TRD-03': () => t('app.page.trd-03', 'Account · Trades'),
  'TRD-04': () => t('app.page.trd-04', 'Account · A/B history'),
  'TRD-05': () => t('app.page.trd-05', 'Account · Revenue'),
  'TRD-06': () => t('app.page.trd-06', 'Account · Behaviour'),
  'TRD-07': () => t('app.page.trd-07', 'Account · Access and devices'),
  'TRD-08': () => t('app.page.trd-08', 'Account · Linked accounts'),
  'TRD-09': () => t('app.page.trd-09', 'Account · Case log'),
  'TOX-01': () => t('app.page.tox-01', 'Toxic flow overview'),
  'TOX-02': () => t('app.page.tox-02', 'Markout analysis'),
  'TOX-03': () => t('app.page.tox-03', 'Coordinated groups'),
  'TOX-04': () => t('app.page.tox-04', 'Coordinated group detail'),
  'TOX-05': () => t('app.page.tox-05', 'News trading'),
  'TOX-06': () => t('app.page.tox-06', 'Bonus and credit abuse'),
  'TOX-07': () => t('app.page.tox-07', 'Swap-free accounts'),
  'TOX-08': () => t('app.page.tox-08', 'Access intelligence'),
  'TOX-09': () => t('app.page.tox-09', 'Detection rules'),
  'CAS-01': () => t('app.page.cas-01', 'Cases'),
  'CAS-02': () => t('app.page.cas-02', 'Case detail'),
  'CAS-03': () => t('app.page.cas-03', 'Evidence snapshot'),
  'CAS-04': () => t('app.page.cas-04', 'Action dialogs'),
  'CAS-05': () => t('app.page.cas-05', 'Pending approvals'),
  'ALR-01': () => t('app.page.alr-01', 'Alerts'),
  'ALR-02': () => t('app.page.alr-02', 'Alert detail'),
  'ALR-03': () => t('app.page.alr-03', 'Alert rules'),
  'ALR-04': () => t('app.page.alr-04', 'Alert rule editor'),
  'REV-01': () => t('app.page.rev-01', 'Revenue overview'),
  'REV-02': () => t('app.page.rev-02', 'Revenue breakdown'),
  'REV-03': () => t('app.page.rev-03', 'Swap reconciliation'),
  'RPT-01': () => t('app.page.rpt-01', 'Reports'),
  'RPT-02': () => t('app.page.rpt-02', 'Report viewer'),
  'RPT-03': () => t('app.page.rpt-03', 'Scheduled reports'),
  'AI-01': () => t('app.page.ai-01', 'Ask Brainux'),
  'AI-02': () => t('app.page.ai-02', 'Daily brief'),
  'SUG-01': () => t('app.page.sug-01', 'Suggestions'),
  'NEW-01': () => t('app.page.new-01', "What's New · Dux FX"),
  'NEW-02': () => t('app.page.new-02', "What's New item"),
  'SET-01': () => t('app.page.set-01', 'Users'),
  'SET-02': () => t('app.page.set-02', 'Roles and permissions'),
  'SET-03': () => t('app.page.set-03', 'Notification channels'),
  'SET-04': () => t('app.page.set-04', 'My profile'),
  'SET-05': () => t('app.page.set-05', 'Groups, books and symbols'),
  'SET-06': () => t('app.page.set-06', 'Costs · Markup'),
  'SET-07': () => t('app.page.set-07', 'Costs · LP swap'),
  'SET-08': () => t('app.page.set-08', 'Costs · Commission'),
  'SET-09': () => t('app.page.set-09', 'General settings'),
  'SET-10': () => t('app.page.set-10', 'Setup health'),
  'SET-11': () => t('app.page.set-11', 'Data API keys'),
  'SET-12': () => t('app.page.set-12', 'Client email templates'),
  'SET-13': () => t('app.page.set-13', 'Audit log'),
  'SET-14': () => t('app.page.set-14', 'AI & integrations'),
};
/** Title of a page in the active language; the registry keeps the English source. */
export const pageTitle = (p: PageDef) => TITLES[p.id]?.() ?? p.title;

const SECTIONS: Record<Section, () => string> = {
  Global: () => t('app.section.global', 'Global'),
  Auth: () => t('app.section.auth', 'Auth'),
  Launcher: () => t('app.section.launcher', 'Launcher'),
  Monitor: () => t('app.section.monitor', 'Monitor'),
  Insight: () => t('app.section.insight', 'Insight'),
  'Dux FX': () => t('app.section.dux-fx', 'Dux FX'),
  Settings: () => t('app.section.settings', 'Settings'),
};
/** Name of a nav section in the active language. */
export const sectionName = (s: Section) => SECTIONS[s]();

export const PAGE_BY_SLUG: Record<string, PageDef> = Object.fromEntries(PAGES.map((p) => [p.slug, p]));
export const pagePath = (id: string): string => '/' + PAGE_BY_ID[id].slug;
